import angular, { IWindowService } from "angular";
import { ColDef } from "../../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { IRowCommandEventArgs } from "../../../../records/grids/controller";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { IReportGroup } from "../../../../reporting/interfaces/IReportGroup";
import {
  ConversionModalService,
  vsServiceName as ConversionModalServiceName,
} from "../../components/conversionModal";

import {
  MachineDetailTabsService,
  vsServiceName as MachineDetailTabsServiceName,
} from "../../components/machineDetailTabs";
import { Machine } from "../../models/machine";
import { MachineConfigurationView } from "../../models/machineConfiguration";
import {
  MachineExportService,
  vsServiceName as MachineExportServiceName,
} from "../../services/machineExportService";

const gridId = Symbol("main.machines.grid");
export class MachinesViewController {
  static $inject = [
    ServerUtilityName,
    GridSettingsServiceName,
    MachineDetailTabsServiceName,
    "$state",
    ConversionModalServiceName,
    "$window",
    ColumnDefinitionServiceName,
    "reportGroup",
    MachineExportServiceName,
  ];
  readonly $state: any;

  readonly gridSettings: IGridSettings;
  readonly defaultQuery: IQuery = {
    CurrentConfiguration: true,
    Statuss: "Warehoused,Floor",
  };
  private gridSelection: any[] = [];
  public isDisplaydecomission: boolean = false;

  readonly additionalColumns: ColDef[];

  constructor(
    private readonly serverUtility: ServerUtility,
    gridSettingsService: GridSettingsService,
    private readonly machineDetailTabsService: MachineDetailTabsService,
    $state: any,
    private readonly conversionModalService: ConversionModalService,
    private readonly $window: IWindowService,
    columnDefinitionService: ColumnDefinitionService,
    readonly reportGroup: IReportGroup,
    private readonly machineExportService: MachineExportService
  ) {
    const firstMachineConfigurationTabSref =
      machineDetailTabsService.getTabSref();
    this.$state = $state;
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.additionalColumns = [
      columnDefinitionService.getSelectColDef({
        headerName: "Select",
      }),
      columnDefinitionService.getLinkColDef({
        headerName: "info",
        iconClass: "fa fa-info-circle text-primary",
        colId: "detail",
        width: 20,
        stateExpression: `${firstMachineConfigurationTabSref}({ machineId: data.MachineId})`,
      }),
      columnDefinitionService.getLinkColDef({
        headerName: "edit",
        iconClass: "fa fa-pencil text-primary",
        colId: "edit",
        stateExpression:
          "main.machines.detail.edit({machineId: data.MachineId, machineConfigurationId: data.Id})",
        requiresPermission: "machineEdit",
      }),
    ];
  }

  machineConfigurationIdSelector(item: MachineConfigurationView) {
    return item.Id;
  }

  gridSelectionChanged(data: any, selected: boolean) {
    const id = data.Id;
    const existingSelectionIndex = this.gridSelection.indexOf(
      this.gridSelection.find((r) => r.Id === id)
    );
    if (!selected && existingSelectionIndex > -1) {
      this.gridSelection.splice(existingSelectionIndex, 1);
      return;
    }
    if (selected && existingSelectionIndex === -1) {
      this.gridSelection.push(data);
    }
  }

  async onItemCommand(
    args: IRowCommandEventArgs<MachineConfigurationView>
  ): Promise<void> {
    const { Id: machineConfigurationId, MachineId: machineId } = args.data;

    switch (args.commandName) {
      case "detail":
        await this.machineDetailTabsService.navigateToTab(machineId);
        break;
      case "edit":
        this.$state.go("^.detail.edit", {
          machineId,
          machineConfigurationId,
        });
    }
  }

  async conversion(): Promise<void> {
    const selectedMachineConfigurationView: MachineConfigurationView =
      this.gridSelection.at(0);
    const modalInstance = this.conversionModalService.openModal();
    const result = await modalInstance.result;

    await this.$state.go("main.machines.detail.conversion", {
      machineId: selectedMachineConfigurationView.MachineId,
      modelMachineId: result.machineId,
      modelMachineConfigurationId: result.machineConfigurationId,
    });
  }

  async decommission(args: IRowCommandEventArgs<Machine>): Promise<void> {
    this.$state.go("^.decommissions", {
      machineConfigurations: this.gridSelection,
    });
  }

  async warehouse(args: IRowCommandEventArgs<Machine>): Promise<void> {
    this.$state.go("^.warehouses", {
      machineConfigurations: this.gridSelection,
    });
  }

  async move(args: IRowCommandEventArgs<Machine>): Promise<void> {
    this.$state.go("^.moves", { machineConfigurations: this.gridSelection });
  }

  addNew(): void {
    this.$state.go("^.add");
  }

  onUpload(csv: string) {
    this.saveFile({
      content: csv,
      filename: "Machine_Upload_Result.csv",
    });
  }

  private getExportQuery() {
    return {
      ...this.gridSettings.getQuery(),
      ...this.defaultQuery,
    };
  }

  async sdsDownload() {
    await this.machineExportService.slotfileDownload(this.getExportQuery());
  }

  async olgDownload() {
    await this.machineExportService.assetMatrixDownload(this.getExportQuery());
  }

  async clone(): Promise<void> {
    const machineId = this.gridSelection[0].MachineId;
    const machineConfigurationId = this.gridSelection[0].Id;

    await this.$state.go("^.add", {
      modelMachineId: machineId,
      modelMachineConfigurationId: machineConfigurationId,
    });
  }

  saveFile(params: { content: string; filename: string }) {
    const blob = new Blob([params.content], { type: "text/csv" });
    const blobUrl = this.$window.URL.createObjectURL(blob);
    const downloadLink = angular.element("<a></a>");
    downloadLink.attr("href", blobUrl);
    downloadLink.attr("download", params.filename);
    downloadLink[0].click();
    downloadLink.remove();
  }

  decomissionChanged(isdecomissionChecked: boolean) {
    this.isDisplaydecomission = isdecomissionChecked;
    if (isdecomissionChecked) {
      this.defaultQuery.Statuss = undefined;
    } else {
      this.defaultQuery.Statuss = "Warehoused,Floor";
    }
  }
}
